/**
 * Wiki SEO helpers — hreflang alternates + JSON-LD for the country
 * encyclopedia.  All page templates use these; authors never touch <head>
 * directly.
 *
 * Hreflang strategy (see ARCHITECTURE §5):
 *   - one <link rel="alternate" hreflang="<code>"> per language in LANGUAGES
 *   - one x-default pointing to the default-language URL
 *
 * JSON-LD strategy:
 *   - Country hub → BreadcrumbList + Place
 *   - Topic article → BreadcrumbList + Article + (optional) FAQPage
 */

import { useEffect } from 'react';
import {
  buildHreflangAlternates,
  buildWikiUrl,
  getLangDef,
  type CountryCode,
  type LangCode,
  type TopicSlug,
} from './architecture';

const PROD_ORIGIN = 'https://lodestar.place';

function resolveOrigin(): string {
  if (typeof window === 'undefined') return PROD_ORIGIN;
  // localhost / preview: prefer current origin so manual QA works.
  return window.location.origin || PROD_ORIGIN;
}

/* ────────────────────────────────────────────────────────────────── */
/*  Hreflang link tags                                                 */
/* ────────────────────────────────────────────────────────────────── */

export interface WikiHreflangOpts {
  cc?: CountryCode;
  topic?: TopicSlug;
}

/**
 * Injects <link rel="alternate" hreflang="..."> tags for every language
 * declared in LANGUAGES + an x-default.  Cleans up on unmount.
 */
export function useWikiHreflang(opts: WikiHreflangOpts): void {
  useEffect(() => {
    const origin = resolveOrigin();
    const alternates = buildHreflangAlternates({ ...opts, origin });

    const owned: HTMLLinkElement[] = [];
    for (const { hreflang, href } of alternates) {
      const selector = `link[rel="alternate"][hreflang="${hreflang}"][data-rm-wiki="1"]`;
      let el = document.head.querySelector<HTMLLinkElement>(selector);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'alternate');
        el.setAttribute('hreflang', hreflang);
        el.setAttribute('data-rm-wiki', '1');
        document.head.appendChild(el);
        owned.push(el);
      }
      el.setAttribute('href', href);
    }

    return () => {
      for (const el of owned) el.remove();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.cc, opts.topic]);
}

/* ────────────────────────────────────────────────────────────────── */
/*  Breadcrumb                                                         */
/* ────────────────────────────────────────────────────────────────── */

interface BreadcrumbItem {
  name: string;
  url: string;
}

function buildBreadcrumbJsonLd(items: BreadcrumbItem[]): unknown {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

/* ────────────────────────────────────────────────────────────────── */
/*  Country hub JSON-LD                                                */
/* ────────────────────────────────────────────────────────────────── */

export interface CountryHubJsonLdOpts {
  cc: CountryCode;
  lang: LangCode;
  countryName: string;
  description: string;
  atlasLabel: string;
}

export function buildCountryHubJsonLd(opts: CountryHubJsonLdOpts): string {
  const origin = resolveOrigin();
  const hubUrl = buildWikiUrl({ cc: opts.cc, lang: opts.lang, origin });
  const atlasUrl = buildWikiUrl({ lang: opts.lang, origin });

  const graph = [
    buildBreadcrumbJsonLd([
      { name: opts.atlasLabel, url: atlasUrl },
      { name: opts.countryName, url: hubUrl },
    ]),
    {
      '@context': 'https://schema.org',
      '@type': 'Place',
      name: opts.countryName,
      description: opts.description,
      url: hubUrl,
      inLanguage: opts.lang,
    },
  ];

  return JSON.stringify({ '@context': 'https://schema.org', '@graph': graph });
}

/* ────────────────────────────────────────────────────────────────── */
/*  Topic article JSON-LD                                              */
/* ────────────────────────────────────────────────────────────────── */

export interface TopicArticleJsonLdOpts {
  cc: CountryCode;
  topic: TopicSlug;
  lang: LangCode;
  countryName: string;
  topicLabel: string;
  atlasLabel: string;
  headline: string;
  description: string;
  /**
   * YYYY-MM-DD, or undefined when the article does not record one.
   *
   * Optional deliberately. These used to be required, and the caller
   * satisfied that by defaulting both to a hardcoded '2026-05-26', which
   * asserted a specific modification date to Google for any article that had
   * never set one. Omitting the field is honest; stating a wrong one is not,
   * and Article.dateModified is a freshness signal that search engines act on.
   */
  datePublished?: string;
  dateModified?: string;
  author: string;
  /** Absolute URL of the hero image — used by Article.image. */
  imageUrl?: string;
  /** Estimated word count (helps Google rank long-form content). */
  wordCount?: number;
  /** Section label (e.g. "Taxes", "Climate") — used by Article.articleSection. */
  section?: string;
  /** Keywords (comma-joined for schema.org/keywords). */
  keywords?: string[];
  faqs?: Array<{ question: string; answer: string }>;
}

export function buildTopicArticleJsonLd(opts: TopicArticleJsonLdOpts): string {
  const origin = resolveOrigin();
  const articleUrl = buildWikiUrl({
    cc: opts.cc, topic: opts.topic, lang: opts.lang, origin,
  });
  const hubUrl = buildWikiUrl({ cc: opts.cc, lang: opts.lang, origin });
  const atlasUrl = buildWikiUrl({ lang: opts.lang, origin });

  const article: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: opts.headline,
    description: opts.description,
    url: articleUrl,
    mainEntityOfPage: articleUrl,
    ...(opts.datePublished ? { datePublished: opts.datePublished } : {}),
    ...(opts.dateModified ? { dateModified: opts.dateModified } : {}),
    author: { '@type': 'Person', name: opts.author },
    publisher: {
      '@type': 'Organization',
      name: 'Lodestar',
      url: origin,
      logo: { '@type': 'ImageObject', url: `${origin}/icon-512.png` },
    },
    inLanguage: opts.lang,
    isPartOf: { '@type': 'WebSite', url: origin, name: 'lodestar.place' },
  };

  if (opts.imageUrl) {
    article.image = { '@type': 'ImageObject', url: opts.imageUrl, width: 1200, height: 630 };
  }
  if (opts.wordCount) {
    article.wordCount = opts.wordCount;
  }
  if (opts.section) {
    article.articleSection = opts.section;
  }
  if (opts.keywords && opts.keywords.length > 0) {
    article.keywords = opts.keywords.join(', ');
  }

  const graph: unknown[] = [
    buildBreadcrumbJsonLd([
      { name: opts.atlasLabel, url: atlasUrl },
      { name: opts.countryName, url: hubUrl },
      { name: opts.topicLabel, url: articleUrl },
    ]),
    article,
  ];

  if (opts.faqs && opts.faqs.length > 0) {
    graph.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: opts.faqs.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    });
  }

  return JSON.stringify({ '@context': 'https://schema.org', '@graph': graph });
}

/* ────────────────────────────────────────────────────────────────── */
/*  OG locale helper                                                   */
/* ────────────────────────────────────────────────────────────────── */

export function getOgLocale(lang: LangCode): string {
  return getLangDef(lang).ogLocale;
}
